import React, { useEffect, useState } from 'react'
import { FaMinus, FaPlus, FaPaypal, FaCcVisa, FaCcMastercard } from 'react-icons/fa';
import '../../Components/Cart/Cart.css'
import { useUser } from '../../store/UserContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, onSnapshot, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/Config';

function Cart() {
  const { currentUser } = useUser()
  const navigate = useNavigate()
  const [cart, setCart] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setLoading(true);
    const cartQuery = query(collection(db, 'cart'), where('userId', '==', currentUser.uid));
    
    const unsubscribe = onSnapshot(cartQuery, 
      (querySnapshot) => {
        const cartData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCart(cartData);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, navigate]);

  const handleDeleteItem = async (itemId) => {
    try {
      const docRef = doc(db, 'cart', itemId)
      await deleteDoc(docRef)
      setCart(prev => prev.filter(item => item.id !== itemId))
    } catch (err) {
      setError(err.message)
    }
  }

  const [updatingItems, setUpdatingItems] = useState({})
  
  const handleQuantityChange = async (itemId, change) => {
    try {
      setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
      
      const docRef = doc(db, 'cart', itemId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Item not found in cart');
      }
      
      const currentQuantity = docSnap.data().quantity;
      const newQuantity = currentQuantity + change;
      
      if (newQuantity < 1) {
        setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
        return;
      }
      
      await updateDoc(docRef, {
        quantity: newQuantity
      });
    
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  if (!currentUser) {
    return <div>Redirecting to login...</div>;
  }

  return (
    <div className="cart-container">
      <h1 className="cart-title">Your Shopping Cart</h1>
      
      <div className="cart-content">
        <div className="cart-items">
          {cart.length === 0 && <div className="empty-cart">Your cart is empty</div>}
          {cart.map((item) => (
            <div className="cart-item" key={item.id}>
              <div className="cart-item-image">
                <img src={item.imageUrl} alt={item.productName} />
              </div>
              <div className="item-details-container">
                <div className="item-details">
                  <h3 className="item-title">{item.productName}</h3>
                  <div className="item-price">₹{item.price.toFixed(2)}</div>
                  <div className="item-availability">{item.stock > 0 ? 'In Stock' : 'Out of Stock'}</div>
                </div>
                <div className="item-actions">
                  <div className="quantity-selector-container">
                    <div className="quantity-selector">
                      <button 
                        className={`quantity-btn minus ${updatingItems[item.id] ? 'loading' : ''}`} 
                        onClick={() => handleQuantityChange(item.id, -1)}
                      >
                        <FaMinus />
                      </button>
                      <input type="number" value={item.quantity} min="1" className="quantity-input" readOnly />
                      <button 
                        className={`quantity-btn plus ${updatingItems[item.id] ? 'loading' : ''}`} 
                        onClick={() => handleQuantityChange(item.id, 1)}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <button className="remove-item" onClick={() => handleDeleteItem(item.id)}>Remove</button>
                  </div>
                </div>
              </div>
              <div className="cart-item-price">
                <h3>Per item total Price</h3>
                <span className="item-total">₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
        
        <div className="cart-summary">
          <div className="summary-card">
            <h3>Order Summary</h3>
            <div className="summary-row">
              <span>Subtotal ({cart.length} items)</span>
              <span>₹{cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span className="free-shipping">₹{cart.reduce((total, item) => total + item.price * item.quantity, 0) <= 1000 ? 'FREE' : '200'}</span>
            </div>
            
            <div className="summary-row total">
              <span>Total</span>
              <span>
                ₹{(
                    cart.reduce((total, item) => total + item.price * item.quantity, 0) +
                    (cart.reduce((total, item) => total + item.price * item.quantity, 0) <= 1000 ? 0 : 200)
                  ).toFixed(2)}
              </span>
            </div>
            <button 
              className="checkout-btn" 
              onClick={() => {
                if (cart.length === 0) {
                  navigate('/')
                } else {
                  navigate('/place-order')
                }
              }}
            >
              Proceed to Checkout
            </button>
            <div className="payment-methods">
              <FaPaypal className="payment-icon" />
              <FaCcVisa className="payment-icon" />
              <FaCcMastercard className="payment-icon" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Cart